import auth, { AuthParams } from '@/utils/authentication';
import { useEffect, useMemo, useState } from 'react';
import {
  PermissionConfigView,
  PermissionCpWordView,
  PermissionMemberView,
  PermissionQAView,
  PermissionRoleView,
  PermissionUserView,
} from './utils/constants';

export type IRoute = AuthParams & {
  name: string;
  key: string;
  // 当前页是否展示面包屑
  breadcrumb?: boolean;
  children?: IRoute[];
  // 当前路由是否渲染菜单项，为 true 的话不会在菜单中显示，但可通过路由地址访问。
  ignore?: boolean;
};

export const routes: IRoute[] = [
  {
    name: '欢迎页',
    key: 'welcome',
    requiredPermissions: [],
  },
  {
    name: '会员管理',
    key: 'member',
    requiredPermissions: [PermissionMemberView],
    oneOfPerm: true,
    children: [
      {
        name: '会员列表',
        requiredPermissions: [PermissionMemberView],
        oneOfPerm: false,
        key: 'member/index',
      },
      {
        name: '会员等级',
        requiredPermissions: [PermissionMemberView],
        oneOfPerm: false,
        key: 'memberLevel/index',
      },
    ],
  },
  {
    name: '财务管理',
    key: 'finance',
    children: [
      {
        name: '会员钱包',
        key: 'finance/wallet',
      },
      {
        name: '钱包流水',
        key: 'finance/walletRecord',
      },
      {
        name: '提现审核',
        key: 'finance/withdraw',
      },
      {
        name: '广告数据',
        key: 'finance/adPromotionData',
      },
    ],
  },
  {
    name: '用户管理',
    key: 'user',
    requiredPermissions: [PermissionUserView, PermissionRoleView],
    oneOfPerm: true,
    children: [
      {
        name: '用户管理',
        requiredPermissions: [PermissionUserView],
        oneOfPerm: false,
        key: 'user/index',
      },
      {
        name: '角色管理',
        requiredPermissions: [PermissionRoleView],
        oneOfPerm: false,
        key: 'role/index',
      },
    ],
  },
  {
    name: '数据管理',
    key: 'data',
    requiredPermissions: [PermissionQAView, PermissionCpWordView],
    oneOfPerm: true,
    children: [
      {
        name: 'CP语录',
        key: 'data/cpWord',
        requiredPermissions: [PermissionCpWordView],
        oneOfPerm: false,
      },
      {
        name: 'QA管理',
        key: 'data/qa',
        requiredPermissions: [PermissionQAView],
        oneOfPerm: false,
      },
      {
        name: '网盘资源',
        key: 'data/movie',
        requiredPermissions: [],
        oneOfPerm: false,
      },
      {
        name: '网盘合集',
        key: 'data/movieTag',
        requiredPermissions: [],
        oneOfPerm: false,
      },
    ],
  },
  {
    name: '系统设置',
    key: 'setting',
    requiredPermissions: [PermissionConfigView],
    oneOfPerm: true,
    children: [
      {
        name: '系统配置',
        key: 'setting/setting',
        requiredPermissions: [PermissionConfigView],
        oneOfPerm: false,
      },
    ],
  },
];

export const getName = (path: string, routes) => {
  return routes.find((item) => {
    const itemPath = `/${item.key}`;
    if (path === itemPath) {
      return item.name;
    } else if (item.children) {
      return getName(path, item.children);
    }
  });
};

const useRoute = (userPermission): [IRoute[], string] => {
  const filterRoute = (routes: IRoute[], arr = []): IRoute[] => {
    if (!routes.length) {
      return [];
    }
    for (const route of routes) {
      const { requiredPermissions, oneOfPerm } = route;
      let visible = true;
      if (requiredPermissions) {
        visible = auth({ requiredPermissions, oneOfPerm }, userPermission);
      }

      if (!visible) {
        continue;
      }
      if (route.children && route.children.length) {
        const newRoute = { ...route, children: [] };
        filterRoute(route.children, newRoute.children);
        if (newRoute.children.length) {
          arr.push(newRoute);
        }
      } else {
        arr.push({ ...route });
      }
    }

    return arr;
  };

  const [permissionRoute, setPermissionRoute] = useState(routes);

  useEffect(() => {
    const newRoutes = filterRoute(routes);
    setPermissionRoute(newRoutes);
  }, [JSON.stringify(userPermission)]);

  const defaultRoute = useMemo(() => {
    const first = permissionRoute[0];
    if (first) {
      const firstRoute = first?.children?.[0]?.key || first.key;
      return firstRoute;
    }
    return '';
  }, [permissionRoute]);

  return [permissionRoute, defaultRoute];
};

export default useRoute;
